import React, { useState } from 'react'
import { FaRegHeart, FaHeart } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { useFavoritePattern } from '../../../../hooks/useFavoritePattern'
import { useRating } from '../../../../hooks/useRating'
import type { PatternCardData } from '../../../../types/types'
import { slugify } from '../../../../utils/slugify'
import { isTombstoned } from '../../../../utils/tombstone'
import { LoginModal } from '../../../common/LoginModal'
import { TombstonePlaceholder } from '../../../common/TombstonePlaceholder'
import { StarRating } from '../../../ui'
import { SmartImage } from '../../../ui/SmartImage'

import styles from './PatternCards.module.css'

interface PatternCardsProps {
  patterns: PatternCardData[]
  onPatternClick?: (pattern: PatternCardData) => void
}

export const PatternCards = ({ patterns, onPatternClick }: PatternCardsProps) => {
  return (
    <div className={styles.grid}>
      {patterns.map((item) => (
        <PatternCard key={item.pattern_id} pattern={item} onPatternClick={onPatternClick} />
      ))}
    </div>
  )
}

interface PatternCardProps {
  pattern: PatternCardData
  onPatternClick?: (pattern: PatternCardData) => void
}

function getDifficultyColor(level: string): string {
  if (!level) return '#444'
  const val = level.toLowerCase()
  if (val.includes('easy') || val.includes('beginner')) return '#1a9763'
  if (val.includes('moderate') || val.includes('intermediate')) return '#F5A623'
  if (val.includes('difficult') && val.includes('very')) return '#de3c3c'
  if (val.includes('difficult')) return '#f56b3c'
  return '#444'
}

const PatternCard = ({ pattern, onPatternClick }: PatternCardProps) => {
  const [hovered, setHovered] = useState(false)
  const navigate = useNavigate()

  // Tombstone (content-lifecycle-and-deletion, task 14.5 / Req 3.2, 3.3): a by-id reference surface
  // (collection contents, favorites) that resolves a retained tombstoned pattern renders a compact
  // "removed by author" entry rather than a dead link. Computed here; the render branches below
  // (after the unconditional hook calls, per the Rules of Hooks).
  const isTombstonedCard = isTombstoned(pattern)
  // Masked reference-surface member (content-visibility-model Req 9.6-9.8, task 32): the surface OWNER is
  // looking at a favorite whose target has gone `private`. The server sends `{ pattern_id, masked: true }`
  // and nothing else, so falling through to the card branch below would paint an empty title, the
  // placeholder image and a zero rating — a worse misrepresentation than the mask. Computed here with the
  // tombstone check; the render branches below, after the unconditional hook calls.
  const isMaskedCard = pattern.masked === true
  const { favorited, loading, showLoginModal, setShowLoginModal, handleFavoriteClick, handleRemoveFavorite } =
    useFavoritePattern(pattern.pattern_id)

  // justification: star_counts from API has string keys '1'-'5' matching StarCounts shape at runtime
  const calculatedRating = useRating(pattern.star_counts as Parameters<typeof useRating>[0])

  // Use direct values if available (search results), otherwise use calculated values
  const averageRating = pattern.average_rating ?? calculatedRating.averageRating
  const totalReviews = pattern.review_count ?? calculatedRating.totalReviews

  const goToPattern = (): void => {
    if (onPatternClick) {
      onPatternClick(pattern)
    } else {
      const slug = slugify(pattern.title)
      const isDraft = pattern.published === false || pattern.GSI3SK?.startsWith('draft#')
      navigate(isDraft ? `/edit/${pattern.pattern_id}/${slug}` : `/pattern/${pattern.pattern_id}/${slug}`)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      goToPattern()
    }
  }

  if (isTombstonedCard) {
    return (
      <div className={styles.card} aria-label="Removed by author">
        <TombstonePlaceholder contentNoun="pattern" variant="entry" />
      </div>
    )
  }

  if (isMaskedCard) {
    // NOTHING clickable: no `onClick`, no `role="button"`, no `tabIndex`, and no `onPatternClick` handoff.
    // The item cannot be opened by this caller (Requirement 4.2 refuses the read), so a control that looked
    // like it might open it would be a lie the user has to click to discover.
    //
    // The removal offer is wired to the EXISTING un-favorite mutation via `useFavoritePattern`. Favorites are
    // the only surface a masked entry can reach today: the mask requires a signature-verified caller who owns
    // the surface, which `shared-profile-fetcher.ts` (secure profile, page 1) and
    // `get-user-favorites-authenticated.ts` (pages 2+) have and the anonymous collection reader does not —
    // `public-collections.ts` resolves members against `PUBLIC_READ_CALLER`, so a private member is OMITTED
    // there rather than masked. If a collection surface ever gains an authenticated member reader, this is
    // where the surface noun and the removal mutation become a prop rather than a constant.
    return (
      <div className={styles.card} aria-label="Saved pattern that is no longer available">
        <TombstonePlaceholder
          contentNoun="pattern"
          variant="masked-entry"
          referenceSurfaceNoun="favorites"
          onRemove={handleRemoveFavorite}
          removePending={loading}
        />
      </div>
    )
  }

  return (
    <>
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <div
        className={styles.card}
        tabIndex={0}
        onClick={goToPattern}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        role="button"
        aria-label={`View details for ${pattern.title}`}
      >
        <div className={styles.imageWrapper}>
          {pattern.images?.[0] ? (
            <SmartImage
              image={pattern.images[0]}
              size="medium"
              alt={pattern.display_description}
              className={styles.cardImage}
              draggable={false}
            />
          ) : (
            <img
              className={styles.cardImage}
              src="/placeholder-pattern.png"
              alt={pattern.display_description}
              draggable={false}
            />
          )}
          {/*
            Private pattern indicator — read from the `visibility` FIELD, not from the `GSI3SK` key prefix
            (content-visibility-model Requirement 3.4; task 30). This is the change `PatternCardData`'s own
            `@deprecated` note on `GSI3SK` asks for by name: "Remove when those components use `visibility`
            instead."

            Three reasons the key prefix was the wrong source, and the third is the one that makes it a defect
            rather than a style point:
              1. The field is the authority for the ACCESS decision; the prefix is the authority for which
                 LISTING an item appears in. This band is an access statement.
              2. Under the derived bucket, an archived private pattern's prefix is `archived#`, so the prefix
                 cannot tell private from unlisted at all and the band would silently stop appearing.
              3. `GSI3SK` is in `INTERNAL_FIELD_NAMES`, so `sanitizeResponse` strips it — on any sanitized
                 payload this band could never have rendered.

            An `unlisted` pattern deliberately gets NO band: it opens for anyone holding the link, and labelling
            it PRIVATE would restate exactly the false claim Requirement 11.6 deletes.
          */}
          {pattern.visibility === 'private' && <div className={styles.privateBand}>PRIVATE</div>}
          {/* Draft pattern indicator */}
          {pattern.GSI3SK?.startsWith('draft#') && (
            <div className={styles.draftBand}>
              DRAFT - Last updated {new Date(pattern.GSI3SK.replace('draft#', '')).toLocaleDateString()}
            </div>
          )}
          <button
            className={`${styles.heart} ${hovered || favorited ? styles.heartVisible : ''}`}
            aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
            onClick={handleFavoriteClick}
            tabIndex={-1}
            type="button"
            disabled={loading}
          >
            {favorited ? <FaHeart color="#e84c5a" /> : <FaRegHeart color="#666" />}
          </button>
        </div>
        <div className={styles.body}>
          <div className={styles.topContent}>
            <div className={styles.title}>{pattern.title}</div>
            <div className={styles.description}>{pattern.display_description}</div>
          </div>
          <div className={styles.bottomContent}>
            <div className={styles.starsRow}>
              <StarRating
                value={averageRating}
                reviewCount={totalReviews}
                showValue={false}
                size={18}
                color="#ffa500"
                starStyle={{ marginRight: 1 }}
              />
              <span className={styles.reviewCount}>({totalReviews})</span>
            </div>
            <div className={styles.meta}>
              <div className={styles.difficulty} style={{ color: getDifficultyColor(pattern.difficulty_level || '') }}>
                {pattern.difficulty_level}
              </div>
              <div className={styles.author}>
                {pattern.screen_name}
                {pattern.author_reputation !== null && pattern.author_reputation !== undefined && (
                  <span className={styles.reputation}> ({pattern.author_reputation})</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PatternCards
